import { useEffect, useState, type ReactNode } from 'react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './alert-dialog';
import { Button } from './button';
import { Input } from './input';
import { Label } from './label';

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  pending?: boolean;
  /**
   * When set, the confirm button stays disabled until the user types this
   * exact string. Reserved for the actions with no undo and a blast radius
   * beyond one object — suspending an account stops every one of its keys at
   * the serving path, and "I clicked the wrong row" is the way that happens.
   */
  confirmPhrase?: string;
  confirmPhraseLabel?: ReactNode;
  onConfirm: () => void;
}

/**
 * The guard on destructive actions.
 *
 * Replaces the inline "Really revoke" button swap in the portal, and the
 * entirely unguarded `Suspend` and `Revoke` buttons in admin — where a
 * mis-click suspended a paying account with no confirmation at all.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  pending = false,
  confirmPhrase,
  confirmPhraseLabel,
  onConfirm,
}: ConfirmDialogProps) {
  const [typed, setTyped] = useState('');

  // Reopening must not inherit the last attempt's typed phrase, or the guard
  // is only a guard the first time.
  useEffect(() => {
    if (!open) setTyped('');
  }, [open]);

  const blocked = confirmPhrase !== undefined && typed.trim() !== confirmPhrase;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {description && <AlertDialogDescription>{description}</AlertDialogDescription>}
        </AlertDialogHeader>

        {confirmPhrase !== undefined && (
          <div className="flex flex-col gap-2">
            <Label htmlFor="confirm-phrase">
              {confirmPhraseLabel ?? (
                <>
                  Type <span className="font-mono">{confirmPhrase}</span> to continue
                </>
              )}
            </Label>
            <Input
              id="confirm-phrase"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoComplete="off"
              spellCheck={false}
              className="font-mono"
            />
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>{cancelLabel}</AlertDialogCancel>
          {/* Not `AlertDialogAction`: that closes the dialog on click, which
              would dismiss it while the mutation is still in flight and hide
              the failure. This closes on success, from the caller. */}
          <Button
            variant={destructive ? 'destructive' : 'default'}
            disabled={pending || blocked}
            onClick={onConfirm}
          >
            {pending ? 'Working…' : confirmLabel}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
